// BookingCard.tsx
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import styled from 'styled-components';
import { t } from 'i18next';
import dayjs from 'dayjs';
import BookingType from './BookingTypeLabel/BookingTypeLabel';
import StyledBookingTopicCard, { CardTypeEnum } from './ContactLabel/ContactLabel';
import BookingTimeSlot from './TimeLabel/TimeLabel';
import BookingAvatar from './BookingAvatar/BookingAvatar';
import BookingClient from './BookingClient/BookingClient';
import {
  BookingStatus,
  BookingTypeEnum,
  cancelBooking,
  IBooking,
} from '../../../../redux/booking/bookingSlice';
import createPermissionCheckers from '../../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../../types/commonTypes';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import BookingCancelLabel from './BookingCancelLabel/BookingCancelLabel';
import CustomModal from '../../../Common/CustomModal';
import CancelBookingModal from '../../CancelBookingModal/CancelBookingModal';
import ResheduleBookingModal from '../../ResheduleBookingModal/ResheduleBookingModal';
import { formatTimeToText } from '../../../../utils/dateUtils';

const StyledBookingTimeSlotWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '0 0 0 10px',
  justifyContent: 'space-between',
  minWidth: '80px',
  flex: 0.08,
}));

const StyledButtonWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  flex: 0.2,
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%',
}));

const StyledCardContent = styled(CardContent)(() => ({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  alignItems: 'center',
  minHeight: '150px',
  position: 'relative',
  justifyContent: 'space-between',
}));

const StyledBookingContactsBox = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  minHeight: '45px',
  padding: '10px 0 0 0',
  justifyContent: 'flex-start',
  maxWidth: '100%',
}));

const StyledButton = styled(Button)<{ isSmallScreen: boolean }>(({ isSmallScreen }) => ({
  height: '35px',
  fontSize: isSmallScreen ? '10px !important' : '14px !important',
  width: isSmallScreen ? '100px !important' : '150px !important',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  border: '2px solid #3B667E !important',
  borderRadius: '10px !important',
  color: '#3B667E !important',
}));

interface BookingCardProps {
  booking: IBooking;
}

const BookingCard: React.FC<BookingCardProps> = ({ booking }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const permissionCheckers = createPermissionCheckers();
  const { cancelBookingLoading, cancelBookingSuccess, updateBookingSuccess } = useAppSelector(
    (state) => state.booking
  );
  const { checkPermissions } = permissionCheckers;
  const dispatch = useAppDispatch();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isResheduleModalOpen, setIsResheduleModalOpen] = useState(false);
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReschedule = () => {
    setIsResheduleModalOpen(true);
    handleMenuClose();
  };

  const handleCancelClick = () => {
    setIsCancelModalOpen(true);
    handleMenuClose();
  };

  const handleCancel = () => {
    dispatch(cancelBooking(booking));
    handleMenuClose();
  };

  const handleCloseModal = () => {
    setIsCancelModalOpen(false);
    setIsResheduleModalOpen(false);
  };

  const handleLink = () => {
    window.open(booking.meetingLink, '_blank');
  };

  useEffect(() => {
    if (cancelBookingSuccess) {
      setIsCancelModalOpen(false);
    }
    if (updateBookingSuccess) {
      setIsResheduleModalOpen(false);
    }
  }, [cancelBookingSuccess, updateBookingSuccess]);

  return (
    <Card variant="outlined" sx={{ border: 'none', px: '10px' }}>
      <StyledCardContent
        sx={{
          '::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '1px',
            backgroundColor: '#7CAEBE',
            transform: 'scaleX(0.95) scaleY(0.5)',
          },
        }}
      >
        <StyledBookingTimeSlotWrapper>
          <BookingTimeSlot bookingAt={booking.date} bookingStatus={booking.bookingStatus} />
          <BookingTimeSlot
            bookingAt={dayjs(booking.date).add(booking.duration, 'minutes').toDate()}
            bookingStatus={booking.bookingStatus}
          />
        </StyledBookingTimeSlotWrapper>
        <Box display="flex" flexDirection="row" width="100%" flex={0.9}>
          <BookingAvatar
            profilePicture={booking.clientFirstName}
            clientName={booking.clientFirstName.charAt(0) + booking.clientLastName.charAt(0)}
          />
          <Box display="flex" flexDirection="column" px="10px" minWidth="100px" flex={0.7}>
            <Box display="flex" flexDirection="row">
              <BookingClient
                clientName={`${booking.clientFirstName} ${booking.clientLastName}`}
                bookingStatus={booking.bookingStatus}
              />
              <BookingType type={booking.servicePackage.bookingType} />
              {booking.bookingStatus === BookingStatus.CANCELED && <BookingCancelLabel />}
            </Box>
            <StyledBookingContactsBox>
              <StyledBookingTopicCard
                text={`${booking.servicePackage.title} - ${formatTimeToText(booking.duration)}`}
                type={CardTypeEnum.TOPIC}
                bookingStatus={booking.bookingStatus}
              />
              {booking.clientPhone && (
                <StyledBookingTopicCard
                  text={booking.clientPhone}
                  type={CardTypeEnum.MOBILE}
                  bookingStatus={booking.bookingStatus}
                />
              )}
              {booking.clientEmail && (
                <StyledBookingTopicCard
                  text={booking.clientEmail}
                  type={CardTypeEnum.EMAIL}
                  bookingStatus={booking.bookingStatus}
                />
              )}
              {booking.referenceNote && booking.referenceText && (
                <StyledBookingTopicCard
                  text={booking.referenceNote}
                  url={booking.referenceText}
                  type={CardTypeEnum.REFERENCE}
                  bookingStatus={booking.bookingStatus}
                />
              )}
            </StyledBookingContactsBox>
          </Box>
          <StyledButtonWrapper>
            {booking.servicePackage.bookingType === BookingTypeEnum.ONLINE &&
              checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_BOOKINGS) &&
              booking.bookingStatus === BookingStatus.CONFIRMED &&
              booking.meetingLink && (
                <StyledButton onClick={handleLink} isSmallScreen={isSmallScreen} variant="outlined">
                  {t('bookings.JoinMeeting')}
                </StyledButton>
              )}
            {checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_BOOKINGS) &&
              booking.bookingStatus === BookingStatus.CONFIRMED && (
                <IconButton onClick={handleMenuOpen} sx={{ float: 'right' }}>
                  <MoreVertIcon />
                </IconButton>
              )}
          </StyledButtonWrapper>
        </Box>
        {checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_BOOKINGS) &&
          booking.bookingStatus === BookingStatus.CONFIRMED && (
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleReschedule}>
                {t('bookings.bookingSettings.resheduleMeeting')}
              </MenuItem>
              <MenuItem onClick={handleCancelClick}>
                {t('bookings.bookingSettings.cancelMeeting')}
              </MenuItem>
            </Menu>
          )}
      </StyledCardContent>

      {isCancelModalOpen && checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_BOOKINGS) && (
        <CustomModal isOpen={isCancelModalOpen} xs={3.3}>
          <CancelBookingModal
            handleDelete={handleCancel}
            handleDismiss={handleCloseModal}
            bookingName={booking.servicePackage.title}
            loading={cancelBookingLoading}
            loadingText={t('bookings.cancelling')}
          />
        </CustomModal>
      )}
      {isResheduleModalOpen &&
        checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_BOOKINGS) && (
          <CustomModal isOpen={isResheduleModalOpen} xs={3}>
            <ResheduleBookingModal
              handleDismiss={handleCloseModal}
              booking={booking}
              loadingText={t('bookings.bookingSettings.resheduling')}
            />
          </CustomModal>
        )}
    </Card>
  );
};

export default BookingCard;
